import { useState, useEffect, lazy, Suspense } from 'react';
import scrollToElement from 'scroll-to-element';
import NavigationBar from './components/navigationBar';

import Intro from './slides/Intro';
import { workPlaces } from './data/work';

import classNames from './styles.module.css';
import Slide from './components/slide';

// Lazy load slide components
const BMG = lazy(() => import('./slides/BMG'));
const Channel4 = lazy(() => import('./slides/Channel4'));
const DeepMind = lazy(() => import('./slides/DeepMind'));
const GoogleHealth = lazy(() => import('./slides/GoogleHealth'));
const Nokia = lazy(() => import('./slides/Nokia'));
const Phaidra = lazy(() => import('./slides/Phaidra'));
const Symbian = lazy(() => import('./slides/Symbian'));
const Synthesia = lazy(() => import('./slides/Synthesia'));

const animate = true;

// Fallback component for lazy-loaded slides
function SlideFallback() {
  return (
    <div
      style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      role="status"
      aria-live="polite"
      aria-label="Loading content"
    >
      Loading...
    </div>
  );
}

function App() {
  const [currentSlide, setCurrentSlide] = useState<string>(document.location.hash.substring(1));

  // Per-route meta map
  const metaMap: Record<
    string,
    { title: string; description: string; url?: string; image?: string }
  > = {
    '': {
      title: 'John-Paul Holt — Portfolio',
      description: 'Portfolio website for John-Paul Holt — UX & Front End Engineer',
      url: 'https://jpholt.dev/',
      image: 'https://jpholt.dev/resources/promo.jpg',
    },
    synthesia: {
      title: 'Synthesia — John-Paul Holt',
      description: 'Synthesia project — animations and visuals',
      url: 'https://jpholt.dev/',
      image: 'https://jpholt.dev/resources/promo.jpg',
    },
    phaidra: {
      title: 'Phaidra — John-Paul Holt',
      description: 'Phaidra project overview and highlights',
      url: 'https://jpholt.dev/',
      image: 'https://jpholt.dev/resources/promo.jpg',
    },
    'google-health': {
      title: 'Google Health — John-Paul Holt',
      description: 'Google Health project — device integrations',
      url: 'https://jpholt.dev/',
      image: 'https://jpholt.dev/resources/promo.jpg',
    },
    deepmind: {
      title: 'DeepMind — John-Paul Holt',
      description: 'DeepMind project — data visualisation and interaction',
      url: 'https://jpholt.dev/',
      image: 'https://jpholt.dev/resources/promo.jpg',
    },
    channel4: {
      title: 'Channel 4 — John-Paul Holt',
      description: 'Channel 4 — broadcast and motion design',
      url: 'https://jpholt.dev/',
      image: 'https://jpholt.dev/resources/promo.jpg',
    },
    nokia: {
      title: 'Nokia — John-Paul Holt',
      description: 'Nokia project — UI, motion and product demos',
      url: 'https://jpholt.dev/',
      image: 'https://jpholt.dev/resources/promo.jpg',
    },
    symbian: {
      title: 'Symbian — John-Paul Holt',
      description: 'Symbian project retrospective',
      url: 'https://jpholt.dev/',
      image: 'https://jpholt.dev/resources/promo.jpg',
    },
    bmg: {
      title: 'BMG — John-Paul Holt',
      description: 'BMG project — creative visuals and sound',
      url: 'https://jpholt.dev/',
      image: 'https://jpholt.dev/resources/promo.jpg',
    },
  };

  const scrollTo = (slide: string) => {
    const el = document.getElementById(`slide-${slide}`);

    if (el) {
      scrollToElement(el, { ease: 'outQuart', offset: 0 });
    }
  };

  useEffect(() => {
    scrollTo(currentSlide);
    if (document.location.hash !== currentSlide) {
      document.location.hash = currentSlide;
      setCurrentSlide(document.location.hash.substring(1));
    }
  }, [currentSlide]);

  const handleNavigation = (id: string): void => {
    setCurrentSlide(id);
    scrollTo(currentSlide);
  };

  return (
    <>
      <NavigationBar items={workPlaces} onNavigate={handleNavigation} currentSlide={currentSlide} />
      <main id="slides" className={classNames.portfolio} aria-label="Portfolio sections">
        <Slide id="" meta={metaMap['']}>
          <Intro animate={animate} onNavigate={handleNavigation} />
        </Slide>
        <Suspense fallback={<SlideFallback />}>
          <Slide id="synthesia" meta={metaMap['synthesia']}>
            <Synthesia />
          </Slide>
        </Suspense>
        <Suspense fallback={<SlideFallback />}>
          <Slide id="phaidra" meta={metaMap['phaidra']}>
            <Phaidra />
          </Slide>
        </Suspense>
        <Suspense fallback={<SlideFallback />}>
          <Slide id="google-health" meta={metaMap['google-health']}>
            <GoogleHealth />
          </Slide>
        </Suspense>
        <Suspense fallback={<SlideFallback />}>
          <Slide id="deepmind" meta={metaMap['deepmind']}>
            <DeepMind />
          </Slide>
        </Suspense>
        <Suspense fallback={<SlideFallback />}>
          <Slide id="channel4" meta={metaMap['channel4']}>
            <Channel4 />
          </Slide>
        </Suspense>
        <Suspense fallback={<SlideFallback />}>
          <Slide id="nokia" meta={metaMap['nokia']}>
            <Nokia />
          </Slide>
        </Suspense>
        <Suspense fallback={<SlideFallback />}>
          <Slide id="symbian" meta={metaMap['symbian']}>
            <Symbian />
          </Slide>
        </Suspense>
        <Suspense fallback={<SlideFallback />}>
          <Slide id="bmg" meta={metaMap['bmg']}>
            <BMG />
          </Slide>
        </Suspense>
      </main>
    </>
  );
}

export default App;
